import { changeAmountToBasicPoints, changeDecimalToString, useStores } from '@/utils'
import { useAnchorWallet, useConnection, useWallet } from '@solana/wallet-adapter-react'
import { observer } from 'mobx-react'
import { FC, useEffect, useState } from 'react'
import { AnchorProvider, BN, Program, Wallet } from '@coral-xyz/anchor'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { PublicKey, SystemProgram } from '@solana/web3.js'
import { AqaStakingProgram, MessageType } from '@/types'
import { TOKEN_PROGRAM_ID } from '@solana/spl-token'
import { SettingButton } from '@/components/atoms/SettingButton'
import { TokenInputFieldGroup } from '@/components/molecules/TokenInputFieldGroup'
import stakingIDL from '../../../types/aqa-staking-program/aqa_staking_program.json'
import styles from './index.module.scss'

type StakeValue = {
  aqaAmount: string
}

export const StakeTokenGroup: FC = observer(() => {
  const { viewer, messages } = useStores()
  const wallet = useAnchorWallet()
  const { connection } = useConnection()
  const { publicKey } = useWallet()
  const [isExecuting, setIsExecuting] = useState(false)
  const tokenBalance = publicKey ? viewer.viewer?.tokenBalances[publicKey.toBase58()] : '0.00'
  // const solBalance = publicKey ? viewer.viewer?.solBalance : ''
  const stakeAmount = publicKey ? viewer.viewer?.stakeAmount : '0.00'
  const lockupPeriod = publicKey ? viewer.viewer?.lockupPeriod : new Date()
  const claimAmount = publicKey ? viewer.viewer?.claimAmount : '0.00'

  const initialValue: StakeValue = {
    aqaAmount: '',
  }

  const validationStakeSchema = Yup.object().shape({
    aqaAmount: Yup.number()
      .typeError('This field must be a number')
      .positive()
      .max(Number(tokenBalance))
      .test('max', `amount must be less than or equal to ${tokenBalance}`, (number) => {
        if (!number || !stakeable) {
          return true
        }
        return number <= Number(tokenBalance)
      })
      .test('maxDigitsAfterDecimal', 'This field must have 9 digits after decimal or less', (number) => {
        if (!number) {
          return true
        }
        const numFracStr = changeDecimalToString(number)
        return /^\d+(\.\d{0,9})?$/.test(numFracStr)
      })
      .required('This field is required'),
  })

  const findATA = async () => {
    if (publicKey) {
      await viewer.viewer?.findATAOrUpdateBalance(publicKey, connection)
      await viewer.viewer?.fetchStakeEntry(connection, wallet as Wallet, publicKey)
    }
  }

  useEffect(() => {
    findATA()
  }, [publicKey])

  let buttonText = ''
  let stakeable = false
  if (!connection || !wallet) {
    buttonText = 'Please connect Phantom wallet'
    stakeable = false
  } else if (Number(tokenBalance) <= 0) {
    buttonText = 'No AQA available'
    stakeable = false
  } else {
    buttonText = 'Stake'
    stakeable = true
  }

  let claimButton = ''
  let unstakeButton = ''
  let claimable = false
  if (!connection || !wallet) {
    claimButton = 'Please connect Phantom wallet'
    unstakeButton = 'Please connect Phantom wallet'
    claimable = false
  } else if (Number(stakeAmount) <= 0) {
    claimButton = 'No AQA staked'
    unstakeButton = 'No AQA staked'
    claimable = false
  } else if (lockupPeriod > new Date()) {
    claimButton = 'Your tokens are currently locked'
    unstakeButton = 'Your tokens are currently locked'
    claimable = false
  } else {
    claimButton = 'Claim'
    unstakeButton = 'Claim & Unstake'
    claimable = true
  }

  return (
    <>
      <div className={styles.container}>
        <p className={styles.apr}>APR 30.0%</p>
        <div className={styles.stakedAndAvailable}>
          <div>
            <span className={styles.textPrimary}>Staked</span>
            <br />
            <span className={styles.textBold}>{stakeAmount} AQA</span>
          </div>
          <div className={styles.textEnd}>
            <span className={styles.textPrimary}>Available</span>
            <br />
            <span className={styles.textBold}>{tokenBalance} AQA</span>
          </div>
        </div>
        <div className={styles.prefix}>
          <span className={styles.textPrimary}>Lockup duration</span>
          <br />
          <span className={styles.textBold}>31 days</span>
        </div>
        <Formik
          initialValues={initialValue}
          validationSchema={validationStakeSchema}
          onSubmit={async (values: StakeValue, actions) => {
            actions.setSubmitting(true)
            setIsExecuting(true)

            if (!connection || !wallet || !publicKey) {
              return
            }

            try {
              const provider = new AnchorProvider(connection, wallet, {})

              const mint = new PublicKey(process.env.NEXT_PUBLIC_AQA_TOKEN_MINT_PUBLICKEY)
              const stakePool = new PublicKey(process.env.NEXT_PUBLIC_STAKING_STAKE_POOL)
              const stakeVault = new PublicKey(process.env.NEXT_PUBLIC_STAKING_STAKE_VAULT)
              const stakeEntrySeed = process.env.NEXT_PUBLIC_STAKE_ENTRY_SEED
              const program = new Program<AqaStakingProgram>(stakingIDL, provider)
              const stakeEntry = PublicKey.findProgramAddressSync(
                [Buffer.from(stakeEntrySeed, 'utf-8'), stakePool.toBuffer(), publicKey.toBuffer()],
                program.programId
              )[0]
              const changedAmount = changeAmountToBasicPoints(values.aqaAmount)
              const amount = new BN(changedAmount)
              const stakerTokenAddress = viewer.viewer?.associatedTokenAddresses[publicKey.toBase58()].ata

              const tx = await program.methods
                .stake(amount)
                .accountsStrict({
                  owner: publicKey,
                  mint,
                  from: stakerTokenAddress,
                  stakeVault,
                  stakePool,
                  stakeEntry,
                  systemProgram: SystemProgram.programId,
                  tokenProgram: TOKEN_PROGRAM_ID,
                })
                .rpc()
              const latestBlockHash = await connection.getLatestBlockhash()

              await connection.confirmTransaction({
                ...latestBlockHash,
                signature: tx,
              })

              if (tx) {
                messages.add({
                  type: MessageType.Info,
                  body: 'Staked AQA.',
                  isTranslated: true,
                  ttl: 5000,
                })
              }
            } catch {
              messages.add({
                type: MessageType.Error,
                body: 'Failed to stake.',
                isDismissable: true,
                ttl: 5000,
              })
            }

            await findATA()
            actions.setSubmitting(false)
            setIsExecuting(false)
          }}
        >
          {({ isSubmitting, isValid }) => {
            return (
              <Form>
                <div className={styles.field}>
                  <Field
                    name='aqaAmount'
                    placeholder='0'
                    color='background'
                    balance={tokenBalance}
                    component={TokenInputFieldGroup}
                  />
                </div>
                <div className={styles.submit}>
                  <SettingButton
                    isLoading={isSubmitting}
                    disabled={isSubmitting || !stakeable || !isValid || isExecuting}
                    buttonSize='medium'
                    buttonType='submit'
                    color='aqa'
                  >
                    {buttonText}
                  </SettingButton>
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>

      {viewer.viewer?.stakeEntry && (
        <div className={styles.container}>
          <p className={styles.apr}>Claim, Unstake</p>
          <div className={styles.prefix}>
            <span className={styles.textPrimary}>Staked</span>
            <br />
            <span className={styles.textBold}>{stakeAmount} AQA</span>
          </div>
          <div className={styles.prefix}>
            <span className={styles.textPrimary}>Lockup period</span>
            <br />
            <span className={styles.textBold}>{lockupPeriod.toUTCString()}</span>
          </div>
          <div className={styles.prefix}>
            <span className={styles.textPrimary}>Estimated Claimable Rewards</span>
            <br />
            <span className={styles.textBold}>~ {claimAmount} AQA</span>
          </div>
          <Formik
            initialValues={{}}
            onSubmit={async (_, actions) => {
              actions.setSubmitting(true)
              setIsExecuting(true)

              if (!connection || !wallet || !publicKey) {
                return
              }

              try {
                const provider = new AnchorProvider(connection, wallet, {})

                const mint = new PublicKey(process.env.NEXT_PUBLIC_AQA_TOKEN_MINT_PUBLICKEY)
                const stakePool = new PublicKey(process.env.NEXT_PUBLIC_STAKING_STAKE_POOL)
                const rewardVault = new PublicKey(process.env.NEXT_PUBLIC_STAKING_REWARD_VAULT)
                const stakeEntrySeed = process.env.NEXT_PUBLIC_STAKE_ENTRY_SEED
                const program = new Program<AqaStakingProgram>(stakingIDL, provider)
                const stakeEntry = PublicKey.findProgramAddressSync(
                  [Buffer.from(stakeEntrySeed, 'utf-8'), stakePool.toBuffer(), publicKey.toBuffer()],
                  program.programId
                )[0]
                const stakerTokenAddress = viewer.viewer?.associatedTokenAddresses[publicKey.toBase58()].ata

                const tx = await program.methods
                  .claim()
                  .accountsStrict({
                    owner: publicKey,
                    mint,
                    to: stakerTokenAddress,
                    rewardVault,
                    stakePool,
                    stakeEntry,
                    systemProgram: SystemProgram.programId,
                    tokenProgram: TOKEN_PROGRAM_ID,
                  })
                  .rpc()
                const latestBlockHash = await connection.getLatestBlockhash()

                await connection.confirmTransaction({
                  ...latestBlockHash,
                  signature: tx,
                })

                if (tx) {
                  messages.add({
                    type: MessageType.Info,
                    body: 'Claimed.',
                    isTranslated: true,
                    ttl: 5000,
                  })
                }
              } catch (error) {
                console.error(error)
                messages.add({
                  type: MessageType.Error,
                  body: 'Failed to claim.',
                  isDismissable: true,
                  ttl: 5000,
                })
              }

              await findATA()
              actions.setSubmitting(false)
              setIsExecuting(false)
            }}
          >
            {({ isSubmitting, isValid }) => {
              return (
                <Form>
                  <div className={styles.submit}>
                    <SettingButton
                      isLoading={isSubmitting}
                      disabled={isSubmitting || !claimable || !isValid || isExecuting}
                      buttonSize='medium'
                      buttonType='submit'
                      color='aqa'
                    >
                      {claimButton}
                    </SettingButton>
                  </div>
                </Form>
              )
            }}
          </Formik>

          <Formik
            initialValues={{}}
            onSubmit={async (_, actions) => {
              actions.setSubmitting(true)
              setIsExecuting(true)

              if (!connection || !wallet || !publicKey) {
                return
              }

              try {
                const provider = new AnchorProvider(connection, wallet, {})

                const mint = new PublicKey(process.env.NEXT_PUBLIC_AQA_TOKEN_MINT_PUBLICKEY)
                const stakePool = new PublicKey(process.env.NEXT_PUBLIC_STAKING_STAKE_POOL)
                const stakeVault = new PublicKey(process.env.NEXT_PUBLIC_STAKING_STAKE_VAULT)
                const rewardVault = new PublicKey(process.env.NEXT_PUBLIC_STAKING_REWARD_VAULT)
                const stakeEntrySeed = process.env.NEXT_PUBLIC_STAKE_ENTRY_SEED
                const program = new Program<AqaStakingProgram>(stakingIDL, provider)
                const stakeEntry = PublicKey.findProgramAddressSync(
                  [Buffer.from(stakeEntrySeed, 'utf-8'), stakePool.toBuffer(), publicKey.toBuffer()],
                  program.programId
                )[0]
                const stakerTokenAddress = viewer.viewer?.associatedTokenAddresses[publicKey.toBase58()].ata

                const tx = await program.methods
                  .unstake()
                  .accountsStrict({
                    owner: publicKey,
                    mint,
                    to: stakerTokenAddress,
                    stakeVault,
                    rewardVault,
                    stakePool,
                    stakeEntry,
                    systemProgram: SystemProgram.programId,
                    tokenProgram: TOKEN_PROGRAM_ID,
                  })
                  .rpc()
                const latestBlockHash = await connection.getLatestBlockhash()

                await connection.confirmTransaction({
                  ...latestBlockHash,
                  signature: tx,
                })

                if (tx) {
                  messages.add({
                    type: MessageType.Info,
                    body: 'Claimed.',
                    isTranslated: true,
                    ttl: 5000,
                  })
                }
              } catch (error) {
                console.error(error)
                messages.add({
                  type: MessageType.Error,
                  body: 'Failed to claim.',
                  isDismissable: true,
                  ttl: 5000,
                })
              }

              await findATA()
              actions.setSubmitting(false)
              setIsExecuting(false)
            }}
          >
            {({ isSubmitting, isValid }) => {
              return (
                <Form>
                  <div className={styles.submit}>
                    <SettingButton
                      isLoading={isSubmitting}
                      disabled={isSubmitting || !claimable || !isValid || isExecuting}
                      buttonSize='medium'
                      buttonType='submit'
                      color='aqa'
                    >
                      {unstakeButton}
                    </SettingButton>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
      )}
    </>
  )
})
